import type { FormValue } from "../../redux/interfaces/folder.types";
import { allFormsValidators } from "./forms/allFormsValidators";
import { validateEmail, validateEmailLocal } from "./generalValidators";

export type DraftFieldValue = string | undefined | null;

interface SanitizeDraftFormValuesOptions {
  hiddenFieldKeys?: Set<string>;
  currentErrors?: Record<string, string>;
  displayFields?: Record<string, "block" | "hidden">;
  requiredFields?: Record<string, boolean>;
  excludeErroredFields?: boolean;
  respectDisplayState?: boolean;
}

export async function sanitizeDraftFormValues(
  candidateValues: Record<string, DraftFieldValue>,
  fields: FormValue[],
  options: SanitizeDraftFormValuesOptions = {}
): Promise<Record<string, DraftFieldValue>> {
  const {
    hiddenFieldKeys,
    currentErrors = {},
    displayFields = {},
    requiredFields = {},
    excludeErroredFields = true,
    respectDisplayState = true,
  } = options;

  const sanitized: Record<string, DraftFieldValue> = {};

  for (const field of fields) {
    if (hiddenFieldKeys?.has(field.key)) continue;

    const value = candidateValues[field.key];
    if (value === undefined || value === null || value === "") continue;

    if (excludeErroredFields && currentErrors[field.key]) continue;

    if (respectDisplayState && displayFields[field.key] === "hidden") continue;

    const isRequired = requiredFields[field.key] ?? field.required;

    if (field.type === "textarea") {
      const normalizedValue = String(value);
      if (isRequired && normalizedValue.length < 10) continue;
      if (normalizedValue.length > 500) continue;
    }

    // Drafts are sanitized on every debounced change and on restore; swap the
    // DNS-backed email validator for its local-only variant so this path
    // never makes network calls (and never blocks/loses data when the email
    // validation service is slow or down).
    const configuredValidator = allFormsValidators[field.key];
    const fieldValidator =
      configuredValidator === validateEmail
        ? validateEmailLocal
        : configuredValidator;
    if (fieldValidator) {
      try {
        await fieldValidator(null, value);
      } catch {
        continue;
      }
    }

    sanitized[field.key] = value;
  }

  return sanitized;
}
